/**
 * App layout: top bar (all screens), tab navigation (desktop: in the top bar,
 * mobile: fixed bottom tab bar) and the routed page content.
 */

import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useLanguage } from '../../utils/LanguageContext';
import { useAuth } from '../../utils/authUtils';
import { useColorMode } from '../../theme';
import PromoToast from '../promo/PromoToast';
import BrandLogo from './BrandLogo';

const content = {
  en: {
    home: 'Home',
    calculators: 'Calculators',
    projects: 'Projects',
    account: 'Account',
    newEstimate: 'New estimate',
    signIn: 'Sign in',
    switchLanguage: 'Tukar ke Bahasa Malaysia',
    dark: 'Dark mode',
    light: 'Light mode',
  },
  my: {
    home: 'Utama',
    calculators: 'Kalkulator',
    projects: 'Projek',
    account: 'Akaun',
    newEstimate: 'Anggaran baru',
    signIn: 'Log masuk',
    switchLanguage: 'Switch to English',
    dark: 'Mod gelap',
    light: 'Mod cerah',
  },
};

export const NAV_ITEMS = [
  { id: 'home', path: '/app', icon: <HomeRoundedIcon /> },
  { id: 'calculators', path: '/app/calculators', icon: <CalculateRoundedIcon /> },
  { id: 'projects', path: '/app/projects', icon: <FolderRoundedIcon /> },
  { id: 'account', path: '/app/account', icon: <PersonRoundedIcon /> },
];

const activeNavId = (pathname) => {
  if (pathname.startsWith('/app/calculators')) return 'calculators';
  if (pathname.startsWith('/app/projects')) return 'projects';
  if (pathname.startsWith('/app/account')) return 'account';
  return 'home';
};

export const LanguageToggle = () => {
  const { language, changeLanguage } = useLanguage();
  const t = content[language] || content.en;
  return (
    <Tooltip title={t.switchLanguage}>
      <Button
        size="small"
        color="inherit"
        onClick={() => changeLanguage(language === 'en' ? 'my' : 'en')}
        sx={{ minWidth: 0, px: 1.25, color: 'text.secondary', fontWeight: 700, border: '1px solid', borderColor: 'divider' }}
      >
        {language === 'en' ? 'BM' : 'EN'}
      </Button>
    </Tooltip>
  );
};

export const ColorModeToggle = () => {
  const { mode, toggleColorMode } = useColorMode();
  const { language } = useLanguage();
  const t = content[language] || content.en;
  return (
    <Tooltip title={mode === 'dark' ? t.light : t.dark}>
      <IconButton size="small" onClick={toggleColorMode} sx={{ color: 'text.secondary' }}>
        {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const AppShell = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = content[language] || content.en;
  const current = activeNavId(location.pathname);

  // Start each page at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
          <Box onClick={() => navigate('/app')} sx={{ cursor: 'pointer', mr: { md: 3 } }}>
            <BrandLogo />
          </Box>

          {isDesktop && (
            <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
              {NAV_ITEMS.filter((item) => item.id !== 'account').map((item) => {
                const active = current === item.id;
                return (
                  <Button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      color: active ? 'primary.main' : 'text.secondary',
                      bgcolor: active ? 'action.selected' : 'transparent',
                      px: 1.75,
                    }}
                  >
                    {t[item.id]}
                  </Button>
                );
              })}
            </Stack>
          )}
          {!isDesktop && <Box sx={{ flex: 1 }} />}

          <Stack direction="row" spacing={1} alignItems="center">
            {isDesktop && (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate('/app/estimate')}>
                {t.newEstimate}
              </Button>
            )}
            <LanguageToggle />
            <ColorModeToggle />
            {isDesktop &&
              (user ? (
                <Tooltip title={t.account}>
                  <IconButton onClick={() => navigate('/app/account')} sx={{ p: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        bgcolor: current === 'account' ? 'primary.main' : 'action.selected',
                        color: current === 'account' ? 'primary.contrastText' : 'primary.main',
                      }}
                    >
                      {(user.name || user.email || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              ) : (
                <Button variant="outlined" onClick={() => navigate('/auth')}>
                  {t.signIn}
                </Button>
              ))}
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="main">
        <Outlet />
      </Box>

      {!isDesktop && (
        <Paper
          component="nav"
          square
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: '1px solid',
            borderColor: 'divider',
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          <BottomNavigation value={current} showLabels sx={{ height: 64, bgcolor: 'background.paper' }}>
            {NAV_ITEMS.map((item) => (
              <BottomNavigationAction
                key={item.id}
                value={item.id}
                label={t[item.id]}
                icon={item.icon}
                onClick={() => navigate(item.path)}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      <PromoToast />
    </Box>
  );
};

export default AppShell;
